import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFreeTrial } from '../context/FreeTrialContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Mail, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { shouldShowVerifyModal, shouldShowVerifyBanner, DISMISS_KEY } from '../lib/emailVerifyGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Onboarding modal that nudges newly registered (unverified) users to verify their
 * e-mail, which unlocks one free premium video.  The modal NEVER claims/grants the
 * free video itself — it only triggers verification and refreshes the profile.
 */
export default function EmailVerificationModal() {
  const { user, isAuthenticated, refreshUser, accessToken } = useAuth();
  const { freeTrial } = useFreeTrial();
  const freeTrialClaimed = !!freeTrial;

  const [dismissedAt, setDismissedAt] = useState(() => {
    const v = Number(localStorage.getItem(DISMISS_KEY));
    return v > 0 ? v : null;
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);      // 'resend' | 'check' | null
  const [status, setStatus] = useState(null);  // { type: 'success' | 'error', msg }
  const [verified, setVerified] = useState(false);

  const gateArgs = { isAuthenticated, user, freeTrialClaimed, dismissedAt };
  const computedShow = shouldShowVerifyModal(gateArgs);
  const showBanner = shouldShowVerifyBanner(gateArgs) && !open;

  // Open automatically when the gate allows (i.e. not within a recent dismissal).
  useEffect(() => {
    if (computedShow) setOpen(true);
  }, [computedShow]);

  const dismiss = () => {
    const now = Date.now();
    localStorage.setItem(DISMISS_KEY, String(now));
    setDismissedAt(now);
    setOpen(false);
    setStatus(null);
  };

  const resend = async () => {
    setBusy('resend');
    setStatus(null);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setStatus({ type: 'success', msg: 'Ověřovací e-mail odeslán. Zkontroluj svou schránku.' });
    } catch {
      setStatus({ type: 'error', msg: 'Ověřovací e-mail se nepodařilo odeslat. Zkus to prosím za chvíli.' });
    } finally {
      setBusy(null);
    }
  };

  const recheck = async () => {
    setBusy('check');
    setStatus(null);
    try {
      const fresh = await refreshUser();   // re-fetch /auth/me
      if (fresh?.email_verified === true) {
        setVerified(true);
        setStatus({ type: 'success', msg: 'E-mail ověřen. Máš připravené jedno prémiové video zdarma.' });
      } else {
        setStatus({ type: 'error', msg: 'Zatím neověřeno. Klikni na odkaz v e-mailu a zkus to znovu.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Nepodařilo se ověřit stav. Zkus to prosím znovu.' });
    } finally {
      setBusy(null);
    }
  };

  if (!isAuthenticated || !user) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="email-verify-modal">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              {verified
                ? <CheckCircle2 className="w-6 h-6 text-green-500" />
                : <Mail className="w-6 h-6 text-primary" />}
            </div>
            <DialogTitle>{verified ? 'Hotovo! 🎉' : 'Vítej na OneSubu 👋'}</DialogTitle>
            <DialogDescription>
              {verified
                ? 'E-mail ověřen. Máš připravené jedno prémiové video zdarma.'
                : 'Ověř svůj e-mail a odemkni si jedno prémiové video zdarma podle vlastního výběru.'}
            </DialogDescription>
          </DialogHeader>

          {status && (
            <p className={`text-sm ${status.type === 'success' ? 'text-green-500' : 'text-red-400'}`}>
              {status.msg}
            </p>
          )}

          {!verified ? (
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button onClick={resend} disabled={busy !== null} className="w-full gap-2" data-testid="email-verify-resend">
                {busy === 'resend' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Odeslat ověřovací e-mail
              </Button>
              <Button variant="outline" onClick={recheck} disabled={busy !== null} className="w-full gap-2" data-testid="email-verify-recheck">
                {busy === 'check' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Už jsem ověřil, zkontrolovat
              </Button>
            </DialogFooter>
          ) : (
            <DialogFooter>
              <Button onClick={() => setOpen(false)} className="w-full">Super, jdu vybírat</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {showBanner && (
        <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 z-40 max-w-sm sm:max-w-xs mx-auto sm:mx-0">
          <button
            onClick={() => { setVerified(false); setStatus(null); setOpen(true); }}
            className="w-full flex items-center gap-3 bg-card border border-border rounded-lg shadow-lg px-4 py-3 text-left hover:border-primary/40 transition-colors"
            data-testid="email-verify-banner"
          >
            <Mail className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="text-sm text-foreground">Ověř e-mail a získej první prémiové video zdarma.</span>
          </button>
        </div>
      )}
    </>
  );
}
